<template>
	<div
		v-click-outside="clickOffsetHandler"
		:class="[
			$style['app-text-field-container'],
			{
				[$style['app-text-field-container--multiple']]: multiple,
				[$style['app-text-field-container--flip-upward']]: popupPlacement.flipUpward,
				[$style['app-text-field-container--align-right']]: popupPlacement.alignRight
			}
		]"
	>
		<div

			class="input"
			:class="[
				$style['app-text-field'],
				$style['app-text-field--dense'],
				{
					[$style['app-text-field--invalid']]: invalid || typedTextInvalid,
					[$style['app-text-field--disabled']]: isDisabled,
					[$style['app-text-field--focused']]: isFocused,
					[$style['base-date-picker-row-focused']]: isPopupOpen
				}
			]"
		>
			<!-- <input
        v-bind="$attrs"
         :class="[$style['app-text-field__input'], 'input']"
        :value="value"
        @focus="inputFocusHandler"
        @blur="inputBlurHandler"
        @input="inputInputHandler"
        @keydown="inputKeyDownHandler"
      > -->
			<Datetime
				ref="dateTime"
				v-model="dateValue"
				v-bind="$attrs"
				:hide-backdrop="hideBackdrop"
				:auto="auto"
				:input-class="$style['app-text-field__native-input']"
				@close="handleClose"
			>
				<template #before>
					<input
						ref="input"
						type="text"
						autocomplete="off"
						class="input"
						:class="[$style['app-text-field__input']]"
						:value="inputText"
						:name="$attrs.name"
						:placeholder="placeholder"
						:disabled="isDisabled"
						@focus="handleOpen"
						@click="handleOpen"
						@blur="handleInputBlur"
						@input="handleTextInput"
						@keydown.enter.prevent="handleEnter"
						@keydown.esc="handleEscape"
						@keydown.tab="closePopup"
					/>
				</template>
				<template #button-cancel>
					{{ $t('common.actions.cancel') }}
				</template>
				<template #button-confirm>
					{{ $t('common.actions.confirm') }}
				</template>
			</Datetime>
			<div
				v-if="hideClearButton || isDisabled || !dateValue"
				:class="$style['app-text-field__trailing-icon']"
			>
				<FontAwesomeIcon
					:icon="['fal', 'calendar-alt']"
					size="sm"
				/>
			</div>
			<BaseIconButton
				v-if="!hideClearButton && !isDisabled && dateValue"
				:class="$style['app-text-field__clear']"
				@click.stop="inputInputHandler(null), $emit('clear', null)"
			>
				<FontAwesomeIcon
					size="lg"
					:icon="['fas', 'times-circle']"
				/>
			</BaseIconButton>
			<FloatingLabel
				:class="$style['app-text-field__label']"
				:label-text="labelText"
				:is-focused="isFocused"
				:is-floated="isFloated"
			/>
			<NotchedOutline
				:class="$style['app-text-field__outline']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
			<NotchedOutlineIdle
				:class="$style['app-text-field__idle']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
		</div>
		<p
			v-if="helperText"
			:class="$style['app-text-field-helper-text']"
		>
			<template v-if="helperText">
				{{ helperText }}
			</template>
		</p>
	</div>
</template>
<script>
import NotchedOutline from '@c:essentials/NotchedOutline'
import NotchedOutlineIdle from '@c:essentials/NotchedOutlineIdle'
import FloatingLabel from '@c:essentials/FloatingLabel'
import BaseIconButton from '@c:global/BaseIconButton'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Datetime } from 'vue-datetime'
import { DateTime } from 'luxon'

const DATE_FORMATS = ['DD', 'D', 'yyyy-MM-dd', 'yyyy.MM.dd', 'yyyy/MM/dd', 'yyyyMMdd', 'dd.MM.yyyy']
const TIME_FORMATS = ['H:mm', 'T', 't']
const DISPLAY_FORMATS = {
	date: DateTime.DATE_MED,
	time: DateTime.TIME_24_SIMPLE,
	datetime: DateTime.DATETIME_MED
}
const POPUP_OFFSET = 8
let lastPopupSize = { width: 320, height: 440 }

export default {
	name: 'BaseDatePicker',
	components: {
		NotchedOutline,
		NotchedOutlineIdle,
		FloatingLabel,
		BaseIconButton,
		Datetime,
		FontAwesomeIcon
	},
	inheritAttrs: false,
	props: {
		maxLength: {
			type: [String, Number],
			default: ''
		},
		icon: {
			type: [String, Number],
			default: ''
		},
		placeholder: {
			type: [String, Number],
			default: ''
		},
		value: {
			type: [String, Number],
			default: ''
		},
		labelText: {
			type: String,
			default: ''
		},
		helperText: {
			type: String,
			default: null
		},
		multiple: {
			type: [Boolean, String],
			default: false
		},
		invalid: {
			type: [Boolean, String],
			default: false
		},
		maxCharacters: {
			type: Number,
			default: 0
		},
		auto: {
			type: Boolean,
			default: true
		},
		hideClearButton: {
			type: Boolean,
			default: false
		},
		hideBackdrop: {
			type: Boolean,
			default: true
		}
	},
	data: () => ({
		isFocused: false,
		isFloated: false,
		privateValue: null,
		parentTableRow: null,
		isPopupOpen: false,
		closeTimeout: null,
		typedText: null,
		typedTextInvalid: false,
		popupPlacement: {
			flipUpward: false,
			alignRight: false
		}
	}),

	computed: {
		dateValue: {
			get () { return this.value },
			set (val) {
				// vue-datetime emits a normalized copy of the value from its created hook; ignore it
				if (!this.isMounted) return
				this.inputInputHandler(val)
			}
		},
		isDisabled () {
			return this.$attrs.disabled === '' || this.$attrs.disabled
		},
		pickerType () {
			return this.$attrs.type || 'date'
		},
		pickerZone () {
			return this.$attrs.zone || 'local'
		},
		displayValue () {
			if (!this.value) return ''
			const datetime = DateTime.fromISO(String(this.value)).setZone(this.pickerZone)
			if (!datetime.isValid) return ''
			const format = this.$attrs.format || DISPLAY_FORMATS[this.pickerType] || DateTime.DATETIME_MED
			return typeof format === 'string' ? datetime.toFormat(format) : datetime.toLocaleString(format)
		},
		inputText () {
			return this.typedText !== null ? this.typedText : this.displayValue
		}
	},
	watch: {
		isPopupOpen (isOpen) {
			if (isOpen) {
				this.parentTableRow = this.findParentTableRow()
				if (this.parentTableRow) {
					this.parentTableRow.classList.add(this.$style['base-date-picker-row-focused'])
				}
			} else {
				if (this.parentTableRow) {
					this.parentTableRow.classList.remove(this.$style['base-date-picker-row-focused'])
					this.parentTableRow = null
				}
			}
		},
		placeholder: {
			immediate: true,
			handler () {
				if (this.placeholder) {
					this.isFloated = true
				}
			}
		},
		value: {
			immediate: true,
			handler (newVal, oldVal) {
				if (newVal && newVal !== oldVal) {
					this.typedText = null
					this.typedTextInvalid = false
				}
				if (this.value !== null && this.value !== '' || this.placeholder || this.typedText) {
					this.isFloated = true
				} else {
					this.privateValue = null
					this.isFloated = false
				}
			}
		}

	},

	beforeDestroy () {
		if (this.closeTimeout) {
			clearTimeout(this.closeTimeout)
			this.closeTimeout = null
		}
		this.isPopupOpen = false
	},
	created () {
		this.isMounted = false
		if (this.value !== null && this.value.length) {
			this.isFloated = true
		}
	},
	mounted () {
		this.isMounted = true
	},
	methods: {
		handleOpen (event) {
			if (this.isDisabled) return
			this.isFocused = true
			this.isFloated = true
			this.isPopupOpen = true
			if (this.closeTimeout) {
				clearTimeout(this.closeTimeout)
				this.closeTimeout = null
			}
			const picker = this.$refs.dateTime
			if (!picker.isOpen) {
				this.updatePopupPlacement(lastPopupSize)
				picker.isOpen = true
				this.$nextTick(() => {
					const popup = picker.$el.querySelector('.vdatetime-popup:not(.vdatetime-fade-leave-active)')
					if (!popup) return
					lastPopupSize = { width: popup.offsetWidth, height: popup.offsetHeight }
					this.updatePopupPlacement(lastPopupSize)
				})
			}
			this.$emit('focus', event)
		},

		updatePopupPlacement ({ width, height }) {
			const rect = this.$refs.dateTime.$el.getBoundingClientRect()
			const spaceBelow = window.innerHeight - rect.bottom - POPUP_OFFSET
			const spaceAbove = rect.top - POPUP_OFFSET
			this.popupPlacement = {
				flipUpward: height > spaceBelow && spaceAbove > spaceBelow,
				alignRight: rect.left + width > window.innerWidth && rect.right - width >= 0
			}
		},

		handleClose (event) {
			this.isFocused = false
			this.isFloated = !!(this.value || this.privateValue || this.placeholder || this.typedText)
			this.$emit('blur', event)
			if (!this.closeTimeout) {
				this.closeTimeout = setTimeout(() => {
					this.isPopupOpen = false
					this.closeTimeout = null
				}, 400)
			}
		},

		inputFocusHandler (event) {
			this.isFocused = true
			this.isFloated = true
			this.$emit('focus', event)
		},

		inputBlurHandler (event) {
			if (!this.isFocused) return
			this.isFocused = false
			this.isFloated = !!(this.value || this.privateValue || this.placeholder || this.typedText)
			this.$emit('blur', event)
		},

		inputInputHandler (value) {
			this.privateValue = value
			this.typedText = null
			this.typedTextInvalid = false
			if (value !== null && value !== '') {
				this.isFloated = true
			} else {
				this.isFloated = false
			}
			this.$emit('input', value)
		},

		clickOffsetHandler () {
			this.closePopup()
		},

		closePopup () {
			if (this.$refs.dateTime?.isOpen) this.$refs.dateTime.close()
		},

		handleInputBlur (event) {
			this.commitTypedText()
			if (!this.$refs.dateTime?.isOpen) this.inputBlurHandler(event)
		},

		handleEnter () {
			this.commitTypedText()
			this.closePopup()
		},

		handleEscape () {
			this.typedText = null
			this.typedTextInvalid = false
			this.closePopup()
			this.$refs.input.blur()
		},

		handleTextInput (event) {
			this.typedText = event.target.value
			this.typedTextInvalid = false
		},

		commitTypedText () {
			if (this.typedText === null) return
			const text = this.typedText.trim()
			if (text === this.displayValue) {
				this.typedText = null
				this.typedTextInvalid = false
				return
			}
			if (!text) {
				this.inputInputHandler(null)
				this.$emit('clear', null)
				return
			}
			const datetime = this.parseTypedText(text)
			if (!datetime) {
				this.typedTextInvalid = true
				this.$emit('input', null)
				this.$emit('invalid-input', text)
				return
			}
			const picker = this.$refs.dateTime
			picker.datetime = datetime.toUTC()
			picker.emitInput()
		},

		parseTypedText (text) {
			const picker = this.$refs.dateTime
			const type = picker.type
			const parsed = this.matchFormats(text, type, picker.zone, picker.format)
			if (!parsed) return null

			const { datetime, hasTime } = parsed
			const date = type === 'time' ? {} : { year: datetime.year, month: datetime.month, day: datetime.day }
			const time = hasTime ? { hour: datetime.hour, minute: datetime.minute, second: 0, millisecond: 0 } : {}
			const result = picker.popupDate.set({ ...date, ...time })

			const unit = type === 'date' ? 'day' : 'minute'
			const { popupMinDatetime: min, popupMaxDatetime: max } = picker
			if (min && result.startOf(unit) < min.startOf(unit)) return null
			if (max && result.startOf(unit) > max.startOf(unit)) return null
			return result
		},

		matchFormats (text, type, zone, customFormat) {
			const hasTimeInType = type !== 'date'
			const candidates = []
			if (typeof customFormat === 'string') {
				candidates.push({ format: customFormat, hasTime: hasTimeInType })
			}
			if (type === 'time') {
				TIME_FORMATS.forEach(format => candidates.push({ format, hasTime: true }))
			} else {
				if (type === 'datetime') {
					DATE_FORMATS.forEach(date => TIME_FORMATS.forEach(time => {
						candidates.push({ format: `${date} ${time}`, hasTime: true })
						candidates.push({ format: `${date}, ${time}`, hasTime: true })
					}))
				}
				DATE_FORMATS.forEach(format => candidates.push({ format, hasTime: false }))
			}

			for (const { format, hasTime } of candidates) {
				const datetime = DateTime.fromFormat(text, format, { zone })
				if (datetime.isValid) return { datetime, hasTime }
			}

			if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
				const iso = DateTime.fromISO(text, { zone })
				if (iso.isValid) return { datetime: iso, hasTime: hasTimeInType && text.includes('T') }
			}
			return null
		},

		findParentTableRow () {
			let el = this.$el?.parentElement
			while (el) {
				if (el.classList && el.classList.contains('app-table-row')) {
					return el
				}
				el = el.parentElement
			}
			return null
		}

	}
}
</script>
<style lang="scss" module>
.app-text-field-helper-text {
    display: flex;
    align-items: center;
    margin: 0;
    margin-bottom: calc($gutter / 2);
    color: getColor(helper-text);
    font-size: 12px;
    line-height: 1.25rem;
    width: 100%;

    &--right {
        text-align: right;
        justify-self: flex-end;
        flex: 1;
    }
}

.app-text-field-container {
    position: relative;
    text-overflow: ellipsis;
    width: 100%;

    &--multiple {
        padding: 0 $gutter * 1.25;
    }

    &--flip-upward :global(.vdatetime-popup) {
        top: auto;
        bottom: calc(100% + 8px);
    }

    &--align-right :global(.vdatetime-popup) {
        left: auto;
        right: 0;
    }
}

.app-text-field {
    position: relative;
    display: inline-flex;
    width: 100%;
    align-items: flex-end;
    box-sizing: border-box;
    margin: $gutter * 1.25 0;
    height: 56px;
    border: none;
    z-index: 3;

    &:not(.app-text-field--disabled):hover {
        .app-text-field__outline svg path {
            stroke: getColor(text);
        }

        .app-text-field__idle {
            border-color: getColor(text);
        }
    }

    &__label {
        z-index: 1;
    }

    &__clear {
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 3;
        color: getColor(grey);
        font-size: 13px;
        width: 24px;
        height: 24px;
        min-width: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__trailing-icon {
        position: absolute;
        right: 20px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 2;
        color: getColor(helper-text);
        pointer-events: none;
    }

    &__native-input {
        display: none !important;
    }

    &__input {
        z-index: 2;
        display: flex;
        box-sizing: content-box;
        border: none;
        border-radius: 0;
        background-color: transparent;
        font-size: 16px;
        line-height: 1.75rem;
        font-weight: 400;
        letter-spacing: 0.0094em;
        width: 100%;
        height: 30px;
        transition: opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1);
        caret-color: getColor(tertiary);

        &:focus {
            outline: none;
        }

        &:not(:focus, :disabled):hover {
            & ~ .app-text-field__outline {
                border-color: getColor(text);
            }

            & ~ .app-text-field__idle {
                border-color: getColor(text);
            }
        }

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus {
            animation-name: autofill;
            animation-fill-mode: both;
        }

        &[type='number']::-webkit-inner-spin-button,
        &[type='number']::-webkit-outer-spin-button {
            appearance: none;
            margin: 0;
        }
    }

    &:not(.app-text-field--disabled) {
        .app-text-field__idle {
            background-color: getColor(input-background);
        }

        .app-text-field__outline {
            background-color: getColor(input-background);
        }

        .app-text-field__input {
            color: getColor(text);
        }
    }

    &--dense {
        height: 48px;

        .app-text-field__input {
            padding: $gutter 52px $gutter $gutter * 2;
        }
    }

    &--no-helper {
        margin-bottom: $gutter * 1.25;
    }

    &--disabled {
        .app-text-field__idle {
            background-color: getColor(input-background-disabled);
        }

        .app-text-field__outline {
            background-color: getColor(input-background-disabled);
        }

        .app-text-field__label {
            color: getColor(input-label-disabled);
        }

        .app-text-field__input {
            color: getColor(helper-text);
        }

        border-radius: 5px;
    }

    &--focused {
        z-index: 80;
    }

    &--invalid {
        .app-text-field__input {
            caret-color: getColor(danger);
        }

        .app-text-field__outline {
            border-color: getColor(danger) !important;
        }

        .app-text-field__idle {
            border-color: getColor(danger) !important;
        }

        .app-text-field__label {
            color: getColor(danger) !important;
        }

        & ~ .app-text-field-helper-text {
            color: getColor(danger) !important;
        }
    }
}

.base-date-picker-row-focused {
    z-index: 80 !important;
}

@keyframes autofill {
    to {
        color: inherit;
        background: transparent;
    }
}
</style>
